import { defineStore } from "pinia";
import { getIndexedId, dataService } from "@thegetty-private/rad-app-utils";
import * as configHelper from "@/helpers/configHelper";

import { getRelatedResourcesLinks } from "./localActionSupport";
import * as localHelpers from "@/helpers/Exhibition";
import {
  getObjectDescription,
  getDateRange
} from "@thegetty-private/linked-data.js";
import * as localLinkedArtHelpers from "@/helpers/LinkedArt";
import { isGroupTheGetty, getDepartmentInfo } from "@/helpers/Group";
import * as internal from "@/data/internal-ontologies";
import {
  getExhibitionVenueName,
  getExhibitionPlaceName
} from "@/helpers/Place";
import * as activityHelpers from "@/helpers/ExhibitionActivity";
import { transformSearchResultToThumbnailItemWithArtist } from "@/helpers/SearchItemHelper";
let dataUrl = configHelper.getDataUrl("/");
let apiUrl = configHelper.getApiPath("/");
let idManagerBaseUrl = configHelper.getIdManagerDataUrl(dataUrl);

/*
 * allow the overwriting of the app url (testing)
 *
 * @param {string} url
 */
export function setApiPath(url) {
  apiUrl = url;
}

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setDataUrl(url) {
  dataUrl = url;
  idManagerBaseUrl = configHelper.getIdManagerDataUrl(url);
}

/**
 * uses the JSON-LD object and gets all related data
 *
 * @param {object} lodObject - the JSON-LD object
 *
 * @returns {object} an object with routes, slugs, activities, places, etc.
 */
async function fetchData(lodObject) {
  let activityIds = localHelpers.getExhibitionActivityFullIds(lodObject);

  let activities = [];
  let places = [];
  let groups = [];
  let slugs = [];
  let activityParts = [];
  let activitySubParts = [];
  let equivalentPlaces = [];

  // get all of the activities
  try {
    activities = await dataService.getAllById(activityIds, dataUrl);
    // check if activities have subpart activity objects that also need to be retrieved
    activities.forEach((activity) => {
      if (activity && activity.part) {
        activity.part.forEach((part) => activitySubParts.push(part.id));
      }
    });
  } catch (e) {
    console.error(e);
  }

  // get parts (for traveling exhibitions)
  try {
    activityParts = await dataService.getAllById(activitySubParts, dataUrl);
  } catch (e) {
    console.error(e);
  }

  // filter out unneeded activities
  let venues = [...activities, ...activityParts].filter(
    (x) => x != undefined && localHelpers.isTravelingExhibition(x) == false
  );

  // get the places
  try {
    let allStubs = localHelpers.getAllStubs(venues, dataUrl);
    places = await dataService.getAllById(
      allStubs.places.map((stub) => stub.id),
      dataUrl
    );
    places.forEach((data) => {
      if (data.equivalent != undefined) {
        data.equivalent.forEach((equiv) => {
          if (equiv.type == "Person" || equiv.type == "Group") {
            equivalentPlaces.push(equiv.id);
          }
        });
      }
    });
  } catch (e) {
    console.error(e);
  }

  // for the places, get the related groups
  try {
    groups = await dataService.getAllById(equivalentPlaces, dataUrl);
  } catch (e) {
    console.error(e);
  }

  try {
    slugs = await dataService.getSlugs(
      groups,
      idManagerBaseUrl,
      internal.ID_MANAGER_SOURCE_GENERATOR,
      internal.ID_MANAGER_TARGET_GENERATOR
    );
  } catch (e) {
    console.error(e);
  }

  return {
    venues,
    groups,
    places,
    slugs
  };
}

/**
 * builds the venue list from the raw JSON-LD
 *
 * @param {object} object -  an object with a list of venues, groups, places, and slugs
 *
 * @returns {array} - a list of venues for display
 */
function buildVenueList({ venues, groups, places, slugs }) {
  return venues.map((venue) => {
    // should this be inside a try/catch?

    const placeIndexedId = activityHelpers.getPlaceIndexedId(venue);
    let placeObject = places.filter(
      (place) => getIndexedId(place.id) == placeIndexedId
    )[0];

    // from the place get the linked institution
    let groupIndexedId =
      activityHelpers.getEquivalentPlaceIndexedIds(placeObject)[0];

    // from that, get the route
    const hostObject = groups.filter(
      (group) => getIndexedId(group.id) == groupIndexedId
    )[0];

    const slugWithPath = slugs[groupIndexedId];
    let route = undefined;
    if (slugWithPath?.slug) {
      route = {
        name: slugWithPath.path,
        state: {
          indexedId: groupIndexedId,
          slug: slugWithPath.slug
        },
        params: {
          slug: slugWithPath.slug
        }
      };
    }

    return {
      placeName: getExhibitionVenueName(placeObject),
      venueId: venue.id,
      placeId: placeIndexedId,
      location: getExhibitionPlaceName(placeObject),
      institutionName: getExhibitionVenueName(placeObject),
      isGetty: isGroupTheGetty(hostObject),
      route: route,
      groupId: groupIndexedId,
      dates: getDateRange(venue)
    };
  });
}

/**
 * Gets the timespan string for the Exhibitions based on the venues
 *
 * @param {array} venues - a list of venues
 *
 * @returns {string}
 */
function getTimespanString(venues) {
  if (venues.length == 1) {
    let venueData = venues[0];
    if (venueData.timespan) {
      let venueDateRange = getDateRange(venueData);
      return localHelpers.getTimespanString(venueDateRange);
    }
  }
  return localHelpers.getExhibitionTimespan(venues);
}

export const useExhibitionStore = defineStore("exhibition", {
  state: () => ({
    items: {}
  }),
  actions: {
    /**
     *
     * @param {string} indexedId  - the indexed Id
     * @param {function} translate - the translate function
     *
     * @returns {object} - the object that's created for the store
     */
    async addExhibition(indexedId, translate) {
      // does exhibition exist in the store already?
      if (this.items[indexedId]) {
        return;
      }
      // establish exhibition in store
      let lodObject = undefined;
      try {
        lodObject = await dataService.getAllById(indexedId, dataUrl);
        this.items[indexedId] = { id: indexedId };
      } catch (err) {
        console.error(err);
        return;
      }

      const ref = this.items[indexedId];

      // page title - used for metatags & sticky header
      ref.pageTitle = localHelpers.getExhibitionTitle(lodObject);

      ref.description = getObjectDescription(lodObject, "text/markdown");

      ref.link = localHelpers.getExhibitionBrochureUrl(lodObject);
      ref.departmentInfo = undefined;

      let departmentStubs = localHelpers.getExhibitionDepartments(lodObject);
      let departments = await dataService.getAllById(
        departmentStubs.map((stub) => stub.id),
        dataUrl
      );
      if (departments.length > 0) {
        let departmentInfo = [];
        departments.forEach((department) => {
          departmentInfo.push(getDepartmentInfo(department));
        });

        let statement =
          translate("exhibition_page.department_info_prefix") +
          " " +
          translate("exhibition_page.department_info", departmentInfo[0]);

        // handle additional departments if we have more than one
        if (departmentInfo.length > 1) {
          departmentInfo.shift();
          departmentInfo.forEach((department) => {
            statement = statement.concat(
              translate("exhibition_page.department_info.conjunction") +
                " " +
                translate("exhibition_page.department_info", department)
            );
          });
        }
        ref.departmentInfo = statement;
      }

      ref.resources = undefined;

      /**
       * Adds the link to the resource Group
       *
       * @param {string} key  - a string key
       * @param {object} link - a link object
       * @param {object} cards  - a map of id => cards
       */
      function addResourceLink(key, link, cards) {
        if (key in cards == false) {
          cards[key] = {
            id: key,
            title: translate(`exhibition_page.related_resources.${key}`),
            links: []
          };
        }
        cards[key].links.push(link);
      }

      try {
        let relatedResourcesLinks = await getRelatedResourcesLinks(
          indexedId,
          [],
          [],
          idManagerBaseUrl
        );
        const cards = {};
        for (const link of relatedResourcesLinks) {
          if (link.url.includes("getty.edu/news/")) {
            addResourceLink("news_and_stories", link, cards);
          } else if (link.url.includes("getty.edu") == false) {
            addResourceLink("external", link, cards);
          } else {
            addResourceLink("exhibition_documentation", link, cards);
          }
        }

        // ensure cards are sorted
        const cardSort = [
          "exhibition_documentation",
          "news_and_stories",
          "external"
        ];
        ref.resources = Object.values(cards).sort(
          (a, b) => cardSort.indexOf(a.id) - cardSort.indexOf(b.id)
        );
      } catch (e) {
        console.error(e);
      }

      try {
        const { venues, groups, places, slugs } = await fetchData(lodObject);

        ref.dates = getTimespanString(venues);
        let venueList = buildVenueList({
          venues,
          groups,
          places,
          slugs
        });
        ref.venues = venueList.sort(
          (a, b) => new Date(a.dates.gte) - new Date(b.dates.gte)
        );
      } catch (e) {
        console.error(e);
      }

      try {
        let url = apiUrl + "api/exhibitionLocations";
        const response = await dataService.axiosGet(url, undefined, {
          id: indexedId
        });
        if (response && response.data.length) {
          ref.gettyLocationInfo = response.data;
          ref.gettyLocation = localHelpers.getExhibitionGettyLocation(
            response.data,
            translate
          );
        } else {
          const isFutureGetty = ref.venues.filter(
            (venue) => venue.isGetty && Date.parse(venue.dates.gte) > Date.now()
          );

          isFutureGetty && isFutureGetty.length
            ? (ref.futureExhibition = true)
            : (ref.pastExhibition = true);
        }
      } catch (e) {
        console.error(e);
      }

      try {
        let url = apiUrl + "api/search";
        let params = {
          member_of_exhibition_id: indexedId,
          include_deaccessioned: true,
          sort: "title_sort"
        };

        if (configHelper.includePartsInQueries() == false) {
          params["is_standalone"] = true;
        }
        const response = await dataService.axiosGet(url, undefined, {
          ...params,
          size: 500
        });
        if (response && response.data) {
          // if we don't have deaccessioned values in the facet(s), don't enable it in the search
          if (response?.data?.facets?.include_deaccessioned == undefined) {
            delete params.include_deaccessioned;
          }

          delete response.data["facets"];
          delete response.data["took"];
          response.data.data = response.data.data.map((item) => {
            let rec = transformSearchResultToThumbnailItemWithArtist(
              item,
              false,
              translate
            );
            // hack for the current world where the RelatedSearchResults component can transform records too
            rec.isTransformed = true;
            return rec;
          });
          ref.items = response.data;
          ref.items.searchParams = params;
        }
      } catch (e) {
        console.error(e);
      }

      ref.type = lodObject.type;

      // technical metadata
      ref.technicalMetadata = localLinkedArtHelpers.getTechnicalMetadataFields(
        lodObject,
        translate
      );
      return;
    }
  },
  getters: {
    // NOTE: getters do not accept parameters, but can return a function that receives one
    /**
     * Gets the transformed exhibition data object from the store
     * @param {object} state - automatic access to state
     * @return {object}
     * @example
     *  {
     *    description: "",
     *    id: "type/1234",
     *    isGetty: false,
     *    technicalMetadata: {...},
     *    type: "Exhibition"
     *  }
     */
    getExhibition(state) {
      /**
       * [returned function of getter - returns a function that has access to state & can receive a parameter]
       * @param {string} indexedId - indexedId of exhibition to retrieve from store
       */
      return (indexedId) => {
        if (state.items[indexedId]) {
          return state.items[indexedId];
        } else {
          return undefined;
        }
      };
    }
  }
});
